import Backbone from "backbone";
import $ from "jquery";
import * as _ from "underscore";

import * as async from "async";
import * as mustcache from "mustcache";
import * as bookUtils from "shared/bookUtils";
import * as languageMap from "shared/languageMap";

/** Backbone View whose model is a Book object */
const EditDocumentView = Backbone.View.extend({
  tagName: "div",
  id: "editDocumentPanel",
  initialize: function (options) {
    this.options = options;
    this.initialValues = {};

    async.series(
      [
        async (done) => {
          if (!this.model.has("plainText")) {
            await this.model.fetchField("plainText");
            done();
          } else {
            done();
          }
        },
        () => {
          this.render();
        },
      ],
      (err) => {
        if (err) throw err;
      }
    );
  },
  isOwner: function () {
    return (
      (this.model.has("ownerID") &&
        this.options.user.get("_id").toString() ===
          this.model.get("ownerID").toString()) ||
      this.options.user.get("_id").toString() === "50cb58dbf939c7397b000002"
    );
  },
  render: function () {
    /** @type {any} */
    let viewData: { [key: string]: any } = {};

    if (this.isOwner()) {
      viewData = {
        owner: true,
        book: this.model.toJSON(),
        categories: _.map(bookUtils.categories, (name) => ({
          name,
          selected: name === this.model.get("category"),
        })),
        languages: [...languageMap.learningList]
          .sort((a, b) => a.english.localeCompare(b.english))
          .map((language) => ({
            code: language.code,
            name: `${language.english} (${language.name})`,
            selected: language.code === this.model.get("language"),
          })),
      };

      const useEveryNewLine = this.model.get("useEveryNewLine");
      if (typeof useEveryNewLine === "boolean") {
        viewData.ignoreSingleNewLines = !useEveryNewLine;
      } else if (typeof useEveryNewLine === "string") {
        viewData.ignoreSingleNewLines = !JSON.parse(useEveryNewLine);
      }
    }

    mustcache.render("editDocumentView", viewData, (html) => {
      this.$el.html(html);

      // Store initial values
      this.initialValues = {
        title: this.$el.find("#titleInput").val(),
        author: this.$el.find("#authorInput").val(),
        plainText: this.$el.find("#documentText").val(),
        ignoreSingleNewLines: $("#ignoreSingleNewLines").is(":checked"),
        category: this.$el
          .find('#categorySelect option:selected:not(".dummy")')
          .val(),
        language: this.$el
          .find('#bookLanguageSelect option:selected:not(".dummy")')
          .val(),
        aiGenerated: $("#aiGeneratedCheckbox").is(":checked"),
      };

      // Disable save and undo buttons initially
      this.$el.find("#saveDocumentButton").prop("disabled", true);
      this.$el.find("#undoDocumentChangesButton").prop("disabled", true);

      // Add change listeners to all inputs
      this.$el
        .find("#titleInput, #authorInput, #documentText")
        .on("input", () => {
          this.checkForChanges();
        });

      this.$el
        .find("#ignoreSingleNewLines, #aiGeneratedCheckbox")
        .on("change", () => {
          this.checkForChanges();
        });

      this.$el.find("#categorySelect, #bookLanguageSelect").on("change", () => {
        this.checkForChanges();
      });
    });
  },

  events: {
    "click #saveDocumentButton": "saveDocument",
    "click #undoDocumentChangesButton": "undoChanges",
    "keydown #documentText": "preventCtrlSpaceDefault",
  },

  undoChanges: function () {
    this.render();
  },

  preventCtrlSpaceDefault: function (event) {
    if (event.ctrlKey) {
      if (event.which === " ".charCodeAt(0)) {
        event.preventDefault();
      }
    }
  },


  checkForChanges: function () {
    const currentValues = {
      title: this.$el.find("#titleInput").val(),
      author: this.$el.find("#authorInput").val(),
      plainText: this.$el.find("#documentText").val(),
      ignoreSingleNewLines: $("#ignoreSingleNewLines").is(":checked"),
      category: this.$el
        .find('#categorySelect option:selected:not(".dummy")')
        .val(),
      language: this.$el
        .find('#bookLanguageSelect option:selected:not(".dummy")')
        .val(),
      aiGenerated: $("#aiGeneratedCheckbox").is(":checked"),
    };

    let hasChanges = false;
    _.each(currentValues, (value, key) => {
      if (value !== this.initialValues[key]) {
        hasChanges = true;
      }
    });

    this.$el.find("#saveDocumentButton").prop("disabled", !hasChanges);
    this.$el.find("#undoDocumentChangesButton").prop("disabled", !hasChanges);
  },

  saveDocument: function () {
    const that = this;

    const inputs = {
      title: this.$el.find("#titleInput"),
      author: this.$el.find("#authorInput"),
      plainText: this.$el.find("#documentText"),
    };

    const newValues: { [field: string]: any } = {};
    _.each(inputs, (input, field) => {
      // @ts-ignore
      const newValue = input.val();
      if (newValue !== that.model.get(field)) {
        newValues[field] = newValue;
      }
    });

    const useEveryNewLine = !$("#ignoreSingleNewLines").is(":checked");
    if (that.model.get("useEveryNewLine") !== useEveryNewLine) {
      newValues.useEveryNewLine = useEveryNewLine;
      newValues.generatedVersion = 0;
    }

    if (newValues.plainText) {
      newValues.generatedVersion = 0;
    }

    const category = that.$el
      .find('#categorySelect option:selected:not(".dummy")')
      .val();
    if (category && that.model.get("category") !== category) {
      newValues.category = category;
    }

    const language = that.$el
      .find('#bookLanguageSelect option:selected:not(".dummy")')
      .val();
    if (language && that.model.get("language") !== language) {
      newValues.language = language;
    }

    const aiGenerated = $("#aiGeneratedCheckbox").is(":checked");
    if (that.model.get("aiGenerated") !== aiGenerated) {
      newValues.aiGenerated = aiGenerated;
    }

    // Cancel all current listeners to change event to avoid refreshing page before the save is
    // complete (was happening upon language change)
    that.model.off("change:language");

    that.$el
      .find("#saveDocumentButton")
      .prop("disabled", true)
      .text("Saving...");

    that.model.save(newValues, {
      patch: true,
      success: () => {
        if (
          that.options.notificationBar &&
          (newValues.plainText || newValues.useEveryNewLine)
        ) {
          let message = "Document Saved.";
          if (that.model.has("youTubeID")) {
            message =
              "Document Saved. You may need to re-sync the text with the audio.";
          }
          that.options.notificationBar.showMessage({
            message: message,
            element: $(".container"),
          });
        }

        // Re-retch the saved book so that the server regenerates the HTML
        that.model.fetch({
          success: function () {
            // Update initial values after successful save
            that.initialValues = {
              title: that.$el.find("#titleInput").val(),
              author: that.$el.find("#authorInput").val(),
              plainText: that.$el.find("#documentText").val(),
              ignoreSingleNewLines: $("#ignoreSingleNewLines").is(":checked"),
              category: that.$el
                .find('#categorySelect option:selected:not(".dummy")')
                .val(),
              language: that.$el
                .find('#bookLanguageSelect option:selected:not(".dummy")')
                .val(),
              aiGenerated: $("#aiGeneratedCheckbox").is(":checked"),
            };

            that.$el
              .find("#saveDocumentButton")
              .prop("disabled", true)
              .text("Save");
            that.$el.find("#undoDocumentChangesButton").prop("disabled", true);
            if (newValues.language) {
              // Do full page refresh if language was changed due to read-aloud feature
              window.location.reload();
            } else {
              that.options.refreshPage();
            }
          },
        });
      },
    });
  },
});

export default EditDocumentView;
